<template>
  <form ref="formRef" class="overflow-x-hidden px-5 pb-8 pt-5" @submit.prevent="save">
    <slot name="prepend" v-bind="slotsData" />

    <div v-if="isJapan" :class="{ 'flex flex-col': withPersonalInfo }">
      <div v-if="withPersonalInfo" class="w-full">
        <div class="flex justify-between gap-5">
          <VcInput
            v-model="lastName"
            :message="errors.lastName"
            :error="!!errors.lastName"
            :disabled="disabled"
            :label="$t('common.labels.last_name')"
            class="mb-4 w-full"
            :placeholder="$t('common.placeholders.last_name')"
            required
            :maxlength="addressFieldMaxLength"
          />
          <VcInput
            v-model="firstName"
            :message="errors.firstName"
            :error="!!errors.firstName"
            :disabled="disabled"
            :label="$t('common.labels.first_name')"
            class="mb-4 w-full"
            :placeholder="$t('common.placeholders.first_name')"
            required
            :maxlength="addressFieldMaxLength"
          />
        </div>

        <VcInput
          v-model="organization"
          :disabled="disabled"
          :message="errors.organization"
          :error="!!errors.organization"
          :label="$t('common.labels.company_name')"
          :placeholder="$t('common.placeholders.company_name')"
          class="mb-4"
          :maxlength="addressFieldMaxLength"
          required
        />

        <VcInput
          v-model="departmentTL"
          :placeholder="$t('common.placeholders.department')"
          :message="errors.departmentTL"
          :error="!!errors.departmentTL"
          :label="$t('common.labels.department')"
          :maxlength="addressFieldMaxLength"
          class="mb-4 w-full"
        />

        <VcSelect
          v-model="country"
          text-field="name"
          :message="errors.countryCode"
          :error="!!errors.countryCode"
          :disabled="disabled"
          :items="countries"
          :label="$t('common.labels.country')"
          :placeholder="$t('common.placeholders.select_country')"
          :maxlength="addressFieldMaxLength"
          class="mb-4 w-full"
          required
          autocomplete
        />

        <VcInput
          v-model="postalCode"
          :message="errors.postalCode"
          :error="!!errors.postalCode"
          :disabled="disabled"
          :label="$t(`${countryConfig?.zipLabel!}`)"
          :placeholder="$t('common.placeholders.postal_code_jp')"
          class="mb-4 w-full"
          required
          :maxlength="addressFieldMaxLength"
          @input="formatPostalCode"
        />

        <VcSelect
          v-if="countryConfig?.stateLabel && regions.length > 1"
          v-model="region"
          text-field="name"
          :items="regions"
          :message="errors.regionId"
          :error="!!errors.regionId"
          :required="!!regions.length"
          :disabled="disabled || !regions.length"
          :label="$t(`${countryConfig?.stateLabel!}`)"
          :placeholder="$t('common.placeholders.select_region')"
          class="mb-4 w-full"
          autocomplete
        />
        <VcInput
          v-if="countryConfig?.stateLabel && regions.length < 2"
          v-model="regionName"
          :message="errors.regionId"
          :error="!!errors.regionId"
          :placeholder="$t('common.placeholders.prefecture')"
          :disabled="disabled"
          :label="$t(`${countryConfig?.stateLabel!}`)"
          class="mb-4 w-full"
          :maxlength="addressFieldMaxLength"
        />

        <div class="flex gap-5">
          <VcInput
            v-if="countryConfig?.cityLabel"
            v-model="city"
            :message="errors.city"
            :error="!!errors.city"
            :disabled="disabled"
            :label="$t(`${countryConfig?.cityLabel!}`)"
            :placeholder="$t('common.placeholders.city_jp')"
            class="mb-4 w-full"
            :required="requiredCity"
            :maxlength="addressFieldMaxLength"
          />

          <VcSelect
            v-if="cities.length > 1"
            v-model="selectedCity"
            :label="$t('common.labels.select_one')"
            text-field="name"
            :items="cities"
            class="w-full"
            :disabled="disabled"
            autocomplete
          />
        </div>

        <VcInput
          v-model="line1"
          :message="errors.line1"
          :error="!!errors.line1"
          :disabled="disabled"
          :label="$t(`${countryConfig?.addressLabel!}`)"
          :placeholder="$t('common.placeholders.address_line1')"
          class="mb-4"
          required
          :maxlength="addressFieldMaxLength"
        />

        <VcInput
          v-model="buildingTL"
          :placeholder="$t('common.placeholders.building')"
          :label="$t('common.labels.building')"
          :message="errors.buildingTL"
          :error="!!errors.buildingTL"
          class="mb-4 mt-5 w-full"
          :maxlength="buildingFieldMaxLength"
        />

        <div v-if="!isJapan" class="mb-5 w-full">
          <div>
            <button type="button" class="flex w-full text-left" @click="handleToggleAddressLine2">
              <div class="flex items-center gap-1 font-medium text-[--color-neutral-a1] underline">
                <VcIcon :name="addressLine2FormOpen ? 'minus' : 'plus-alt-sm'" :size="7"></VcIcon>
                {{ $t("common.labels.address_line2_jp") }}
              </div>
            </button>

            <div v-show="addressLine2FormOpen" class="mt-5">
              <VcInput
                v-model="mailStopTL"
                :placeholder="$t('common.placeholders.mailstop')"
                :label="$t('common.labels.mailstop')"
                :message="errors.mailStopTL"
                :error="!!errors.mailStopTL"
                class="mt-5 w-full"
                :maxlength="mailStopFieldMaxLength"
              />
            </div>
          </div>
        </div>

        <VcInput
          v-model="phone"
          :message="errors.phone"
          :error="!!errors.phone"
          :disabled="disabled"
          :required="requiredPhone"
          :label="$t('common.labels.phone')"
          :placeholder="$t('common.placeholders.phone')"
          class="mb-4"
          @input="handlePhoneInput"
        />

        <VcInput
          v-model="email"
          :message="errors.email"
          :error="!!errors.email"
          :disabled="disabled"
          :required="requiredEmail"
          :label="$t('common.labels.email')"
          :placeholder="$t('common.placeholders.email')"
          class="mb-4"
          :maxlength="emailFieldMaxLength"
        />

        <div class="flex flex-col gap-5 pb-5 text-base font-medium leading-6 text-[--color-neutral-a1]">
          <VcCheckbox v-if="!props.defaultBillingCheckboxHidden" v-model="isDefaultBillingAddressTL">{{
            $t("common.labels.default_billing_address")
          }}</VcCheckbox>
          <VcCheckbox v-if="!props.defaultShippingCheckboxHidden" v-model="isDefaultShippingAddressTL"
            >{{ $t("common.labels.default_shipping_address") }}
          </VcCheckbox>
        </div>
      </div>
    </div>
    <div v-else :class="{ 'flex flex-col': withPersonalInfo }">
      <div v-if="withPersonalInfo" class="w-full">
        <div class="flex justify-between gap-5">
          <template v-if="isChina">
            <VcInput
              v-model="lastName"
              :message="errors.lastName"
              :error="!!errors.lastName"
              :disabled="disabled"
              :label="$t('common.labels.last_name')"
              class="mb-4 w-full"
              :placeholder="$t('common.placeholders.last_name')"
              required
              :maxlength="addressFieldMaxLength"
            />
            <VcInput
              v-model="firstName"
              :message="errors.firstName"
              :error="!!errors.firstName"
              :disabled="disabled"
              :label="$t('common.labels.first_name')"
              class="mb-4 w-full"
              :placeholder="$t('common.placeholders.first_name')"
              required
              :maxlength="addressFieldMaxLength"
            />
          </template>
          <template v-else>
            <VcInput
              v-model="firstName"
              :message="errors.firstName"
              :error="!!errors.firstName"
              :disabled="disabled"
              :label="$t('common.labels.first_name')"
              class="mb-4 w-full"
              :placeholder="$t('common.placeholders.first_name')"
              required
              :maxlength="addressFieldMaxLength"
            />
            <VcInput
              v-model="lastName"
              :message="errors.lastName"
              :error="!!errors.lastName"
              :disabled="disabled"
              :label="$t('common.labels.last_name')"
              class="mb-4 w-full"
              :placeholder="$t('common.placeholders.last_name')"
              required
              :maxlength="addressFieldMaxLength"
            />
          </template>
        </div>

        <VcInput
          v-model="organization"
          :disabled="disabled"
          :label="$t('common.labels.company_name_optional')"
          :placeholder="$t('common.placeholders.company_name')"
          class="mb-4"
          :maxlength="128"
        />

        <VcSelect
          v-model="country"
          text-field="name"
          :message="errors.countryCode"
          :error="!!errors.countryCode"
          :disabled="disabled"
          :items="countries"
          :label="$t('common.labels.country')"
          :placeholder="$t('common.placeholders.select_country')"
          class="mb-4 w-full"
          required
          autocomplete
        />

        <VcInput
          v-model="line1"
          :message="errors.line1"
          :error="!!errors.line1"
          :disabled="disabled"
          :label="$t(`${countryConfig?.addressLabel!}`)"
          :placeholder="$t(`${countryConfig?.addressLabel!.replace('labels', 'placeholders')}`)"
          class="mb-4"
          required
          :maxlength="addressFieldMaxLength"
        />

        <div class="mb-5 w-full">
          <div>
            <button type="button" class="flex w-full text-left" @click="handleToggleAddressLine2">
              <div class="flex items-center gap-1 font-medium text-[--color-neutral-a1] underline">
                <VcIcon :name="addressLine2FormOpen ? 'minus' : 'plus-alt-sm'" :size="7"></VcIcon>
                {{ $t("common.labels.address_line2") }}
              </div>
            </button>

            <div v-show="addressLine2FormOpen" class="mt-5">
              <VcInput
                v-model="departmentTL"
                :placeholder="$t('common.placeholders.department')"
                :label="$t('common.labels.department')"
                :message="errors.departmentTL"
                :error="!!errors.departmentTL"
                :maxlength="addressFieldMaxLength"
                class="w-full"
              />
              <VcInput
                v-model="buildingTL"
                :placeholder="$t('common.placeholders.building')"
                :label="$t('common.labels.building')"
                :message="errors.buildingTL"
                :error="!!errors.buildingTL"
                :maxlength="buildingFieldMaxLength"
                class="mt-5 w-full"
              />
              <VcInput
                v-model="mailStopTL"
                :placeholder="$t('common.placeholders.mailstop')"
                :label="$t('common.labels.mailstop')"
                :message="errors.mailStopTL"
                :error="!!errors.mailStopTL"
                :maxlength="mailStopFieldMaxLength"
                class="mt-5 w-full"
              />
            </div>
          </div>
        </div>

        <div class="flex gap-5">
          <VcInput
            v-if="countryConfig?.districtLabel"
            v-model="districtTL"
            :disabled="disabled"
            :label="$t(`${countryConfig?.districtLabel!}`)"
            :placeholder="$t(`${countryConfig?.districtLabel!.replace('labels', 'placeholders')}`)"
            :message="errors.districtTL"
            :error="!!errors.districtTL"
            class="mb-4 w-full"
            required
            :maxlength="addressFieldMaxLength"
          />

          <VcSelect
            v-if="districts.length > 1"
            v-model="selectedDistrict"
            :label="$t('common.labels.select_one')"
            text-field="name"
            :items="districts"
            class="w-full"
            :disabled="disabled"
            autocomplete
          />
        </div>

        <VcInput
          v-model="postalCode"
          :message="postalCodeLiveMessage || errors.postalCode"
          :error="!!postalCodeLiveMessage || !!errors.postalCode"
          :disabled="disabled"
          :label="$t(`${countryConfig?.zipLabel!}`)"
          :placeholder="$t(`${countryConfig?.zipLabel!.replace('labels', 'placeholders')}`)"
          class="mb-4 w-full"
          required
          :maxlength="addressFieldMaxLength"
          @input="formatPostalCode"
        />

        <div class="flex gap-5">
          <VcInput
            v-if="countryConfig?.cityLabel"
            v-model="city"
            :message="errors.city"
            :error="!!errors.city"
            :disabled="disabled"
            :label="$t(`${countryConfig?.cityLabel!}`)"
            :placeholder="$t(`${countryConfig?.cityLabel!.replace('labels', 'placeholders')}`)"
            class="mb-4 w-full"
            :required="requiredCity"
            :maxlength="addressFieldMaxLength"
          />

          <VcSelect
            v-if="cities.length > 1"
            v-model="selectedCity"
            :label="$t('common.labels.select_one')"
            text-field="name"
            :items="cities"
            class="w-full"
            :disabled="disabled"
            autocomplete
          />
        </div>

        <VcSelect
          v-if="countryConfig?.stateLabel && regions.length > 1"
          v-model="region"
          text-field="name"
          :items="regions"
          :message="errors.regionId"
          :error="!!errors.regionId"
          :required="!!regions.length"
          :disabled="disabled || !regions.length"
          :label="$t(`${countryConfig?.stateLabel!}`)"
          :placeholder="$t('common.placeholders.select_region')"
          class="mb-4 w-full"
          autocomplete
        />
        <VcInput
          v-if="countryConfig?.stateLabel && regions.length < 2"
          v-model="regionName"
          :message="errors.regionId"
          :error="!!errors.regionId"
          :disabled="disabled"
          :label="$t(`${countryConfig?.stateLabel!}`)"
          :placeholder="$t(`${countryConfig?.stateLabel!.replace('labels', 'placeholders')}`)"
          class="mb-4 w-full"
          :maxlength="addressFieldMaxLength"
          required
        />

        <VcInput
          v-if="countryConfig?.countyLabel"
          v-model="countyTL"
          :message="errors.regionId"
          :error="!!errors.regionId"
          :disabled="disabled"
          :label="$t(`${countryConfig?.countyLabel!}`)"
          :placeholder="$t(`${countryConfig?.countyLabel!.replace('labels', 'placeholders')}`)"
          class="mb-4 w-full"
          :maxlength="addressFieldMaxLength"
        />

        <VcInput
          v-model="phone"
          :message="errors.phone"
          :error="!!errors.phone"
          :disabled="disabled"
          :required="requiredPhone"
          :label="$t('common.labels.phone')"
          :placeholder="$t('common.placeholders.phone')"
          class="mb-4"
          @input="handlePhoneInput"
        />

        <VcInput
          v-model="email"
          :message="errors.email"
          :error="!!errors.email"
          :disabled="disabled"
          :required="requiredEmail"
          :label="$t('common.labels.email')"
          :placeholder="$t('common.placeholders.email')"
          class="mb-4"
          :maxlength="emailFieldMaxLength"
        />

        <div class="flex flex-col gap-5 pb-5 text-base font-medium leading-6 text-[--color-neutral-a1]">
          <VcCheckbox v-if="!props.defaultBillingCheckboxHidden" v-model="isDefaultBillingAddressTL">{{
            $t("common.labels.default_billing_address")
          }}</VcCheckbox>
          <VcCheckbox v-if="!props.defaultShippingCheckboxHidden" v-model="isDefaultShippingAddressTL"
            >{{ $t("common.labels.default_shipping_address") }}
          </VcCheckbox>
        </div>

        <div v-if="shouldTaxExemptNoBeDisplayed" class="mb-5 w-full">
          <div>
            <button type="button" class="flex w-full text-left" @click="handleToggleTaxExemptNo">
              <div class="flex items-center gap-1 font-medium text-[--color-neutral-a1] underline">
                <VcIcon :name="taxExemptNoFormOpen ? 'minus' : 'plus-alt-sm'" :size="7"></VcIcon>
                {{ $t("common.labels.tax_exempt_no") }}
              </div>
            </button>

            <div v-show="taxExemptNoFormOpen">
              <VcInput
                v-model="taxExemptNoTL"
                :placeholder="$t('common.placeholders.tax_exempt_no')"
                class="mt-3 w-full"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
    <slot name="append" v-bind="slotsData" />
  </form>
</template>

<script setup lang="ts">
import { useField, useForm } from "vee-validate";
import { computed, onMounted, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { useGeolocation } from "@/core/composables/useGeolocation";
import { countryConfigsMap } from "@/core/constants/address-country-configs";
import { eligibleVatCountries } from "@/core/constants/eligible-vat-countries";
import { getAddressName, Logger } from "@/core/utilities";
import { useMelissaAddresses } from "@/shared/account/composables/useMelissaAddresses";
import { useFormErrorScroll } from "@/shared/forms/composables/useFormErrorScroll";
import { usePostalCodeValidation } from "@/shared/thorlabs/shared/composables/usePostalCodeValidation";
import { addressSchemas } from "./addressValidationSchemas";
import type {
  CountryRegionType,
  CountryType,
  MelissaResultType,
  MemberAddressTypeExtension,
  QueryGetMelissaAddressesArgs,
} from "@/core/api/graphql/types";
import type { CountryConfigType } from "@/core/types/addressCountryConfigs";
import type { ItemSelectType } from "@/core/types/item-select";

const emit = defineEmits<IEmits>();
const props = withDefaults(defineProps<IProps>(), {
  countries: () => [],
  defaultBillingCheckboxHidden: false,
  defaultShippingCheckboxHidden: false,
  withoutTaxExempt: false,
  isSubmitDisabled: false,
});

interface IProps {
  modelValue?: MemberAddressTypeExtension;
  disabled?: boolean;
  requiredEmail?: boolean;
  requiredPhone?: boolean;
  requiredCity?: boolean;
  withPersonalInfo?: boolean;
  countries?: CountryType[];
  defaultBillingCheckboxHidden?: boolean;
  defaultShippingCheckboxHidden?: boolean;
  withoutTaxExempt?: boolean;
  isSubmitDisabled?: boolean;
}
interface IEmits {
  (event: "update:modelValue", address: MemberAddressTypeExtension): void;
  (event: "save", address: MemberAddressTypeExtension): void;
}
const { t } = useI18n();
const { countryCodeISO3 } = useGeolocation();
const { formRef, scrollToFirstError } = useFormErrorScroll();
const cities = ref<ItemSelectType[]>([]);
const districts = ref<ItemSelectType[]>([]);
const initialValues: MemberAddressTypeExtension = {
  isDefault: false,
  isDefaultShippingAddressTL: false,
  isDefaultBillingAddressTL: false,
  isFavorite: false,
  countyTL: "",
  districtTL: "",
  departmentTL: "",
  buildingTL: "",
  dataAreaIdTL: "",
  eRPIDTL: "",
  firstName: "",
  lastName: "",
  email: "",
  organization: "",
  postalCode: "",
  countryCode: countryCodeISO3.value || "",
  countryName: "",
  regionId: "",
  regionName: "",
  city: "",
  line1: "",
  line2: "",
  phone: "",
  description: "",
  taxExemptNoTL: "",
  mailStopTL: "",
};
const isEditAddress = ref(true);
const isCreateMode = computed(() => !props.modelValue?.id);
const addressFieldMaxLength = ref(36); // Do not accept more than 35 characters in address fields
const addressFieldLimit = addressSchemas.ADDRESS_FIELD_LIMIT;
const buildingFieldMaxLength = ref(26);
const buildingFieldLimit = addressSchemas.BUILDING_FIELD_LIMIT;
const mailStopFieldMaxLength = ref(10);
const mailStopFieldLimit = addressSchemas.MAIL_STOP_FIELD_LIMIT;
const emailFieldMaxLength = ref(51); // Do not accept more than 50 characters in email
const countryCodesThatDisableMelissa = ["ICA"];

const isFormSubmitButtonInvalid = computed(() => {
  if (isCreateMode.value) {
    return props.isSubmitDisabled;
  }
  return props.isSubmitDisabled || !meta.value.valid;
});
const { values, meta, errors, handleSubmit, setErrors, setFieldValue, validate, resetForm } =
  useForm<MemberAddressTypeExtension>({
    initialValues,
  });

const { loadMelissaAddresses } = useMelissaAddresses();

const taxExemptNoFormOpen = ref(false);
const addressLine2FormOpen = ref(false);

const handleToggleTaxExemptNo = () => {
  taxExemptNoFormOpen.value = !taxExemptNoFormOpen.value;
};

const handleToggleAddressLine2 = () => {
  addressLine2FormOpen.value = !addressLine2FormOpen.value;
};

const slotsData = computed(() => ({
  setErrors,
  setFieldValue,
  validate,
  reset: resetForm,
  save,
  errors,
  values,
  dirty: meta.value.dirty,
  valid: !isFormSubmitButtonInvalid.value,
  validated: meta.value.validated,
  pending: meta.value.pending,
  touched: meta.value.touched,
}));

const isJapan = computed<boolean>(() => country.value?.id === "JPN");
const isChina = computed<boolean>(() => country.value?.id === "CHN");
const isCanada = computed<boolean>(() => country.value?.id === "CAN");

const { validatePostalCode, getPostalCodePrefix } = usePostalCodeValidation();

const postalCodeLiveMessage = computed(() => {
  if (!isCanada.value) {
    return undefined;
  }

  const compact = (postalCode.value ?? "").replace(/\s/g, "");
  if (compact.length > 0 && compact.length !== 6) {
    return t("common.messages.postal_code_must_be_6");
  }

  return undefined;
});

const emailRules = computed(() => {
  return addressSchemas.createEmailTypedSchema({
    t,
    withPersonalInfo: props.withPersonalInfo,
    requiredEmail: props.requiredEmail,
  });
});

const phoneRules = computed(() => {
  return addressSchemas.createPhoneTypedSchema({
    t,
    withPersonalInfo: props.withPersonalInfo,
    requiredPhone: props.requiredPhone,
  });
});

const handlePhoneInput = (event: Event) => {
  const input = event.target as HTMLInputElement;
  const value = input.value;

  // Count only non-formatting characters
  const nonFormattingChars = value.replace(/[ ()+-/]/g, "");

  // If already at 15 digits and trying to add more digits, prevent it
  if (nonFormattingChars.length >= 16) {
    // Get the previous valid value by keeping only the first 15 digits plus formatting chars
    let digitCount = 0;
    let newValue = "";

    for (let i = 0; i < value.length; i++) {
      const char = value.charAt(i);
      if (/[0-9]/.test(char)) {
        if (digitCount < 16) {
          newValue += char;
          digitCount++;
        }
      } else if (/[ ()+-/]/.test(char)) {
        newValue += char;
      }
    }

    // Update the input value
    phone.value = newValue;
  }
};

const organizationRules = computed(() => {
  return addressSchemas.createOrganizationTypedSchema({ countryId: country.value?.id });
});

// Do not use computed based on field value cause it may cause infinite loop
const cityRules = computed(() => {
  const config = countryConfigsMap[values.countryCode || ""] || countryConfigsMap["DEFAULT"];
  return addressSchemas.createCityTypedSchema({
    t,
    requiredCity: props.requiredCity,
    isCityVisible: !!config.cityLabel,
  });
});

const shouldTaxExemptNoBeDisplayed = computed(() => {
  return eligibleVatCountries.includes(country.value?.id || "") && !props.withoutTaxExempt;
});

const regionRules = computed(() => {
  const config = countryConfigsMap[values.countryCode || ""] || countryConfigsMap["DEFAULT"];
  return addressSchemas.createRegionTypedSchema({
    countries: props.countries,
    isRegionVisible: !!config.stateLabel,
  });
});

const firstNameRules = computed(() => {
  return addressSchemas.createFirstNameTypedSchema({ t, withPersonalInfo: props.withPersonalInfo });
});

const lastNameRules = computed(() => {
  return addressSchemas.createLastNameTypedSchema({ t, withPersonalInfo: props.withPersonalInfo });
});

const country = computed<CountryType | undefined>({
  get: () => props.countries.find((item) => countryCode.value === item.id),
  set: (value?: CountryType) => {
    countryCode.value = value?.id ?? "";
    countryName.value = value?.name ?? "";
    regionId.value = "";
    regionName.value = "";
  },
});

const countryConfig = computed<CountryConfigType | undefined>(() => {
  if (country.value) {
    return countryConfigsMap[country.value.id] || countryConfigsMap["DEFAULT"];
  }

  return countryConfigsMap["DEFAULT"];
});

const regions = computed<CountryRegionType[]>(() => country.value?.regions ?? []);
const melissaRegions = ref<CountryRegionType[]>([]);

const region = computed<CountryRegionType | undefined>({
  get: () => {
    if (regions.value.length === 0 && melissaRegions.value.length > 0) {
      return melissaRegions.value.find((item) => regionId.value === item.id);
    }

    return regions.value.find((item) => regionId.value === item.id || regionName.value === item.name);
  },
  set: (value?: CountryRegionType) => {
    regionId.value = value?.id ?? "";
    regionName.value = value?.name ?? "";
  },
});

const selectedCity = computed<ItemSelectType | undefined>({
  get: () => cities.value.find((item) => city.value === item.name),
  set: (value?: ItemSelectType) => {
    city.value = value?.name ?? "";
  },
});

const selectedDistrict = computed<ItemSelectType | undefined>({
  get: () => districts.value.find((item) => districtTL.value === item.name),
  set: (value?: ItemSelectType) => {
    districtTL.value = value?.name ?? "";
  },
});

const { value: email } = useField("email", emailRules);
const { value: city } = useField("city", cityRules);
const { value: phone } = useField("phone", phoneRules);
const { value: firstName } = useField("firstName", firstNameRules);
const { value: lastName } = useField("lastName", lastNameRules);
const { value: departmentTL } = useField(
  "departmentTL",
  computed(() => addressSchemas.createMaxLengthTypedSchema(addressFieldLimit, t("common.messages.character_limit_35"))),
);
const { value: buildingTL } = useField(
  "buildingTL",
  computed(() =>
    addressSchemas.createMaxLengthTypedSchema(buildingFieldLimit, t("common.messages.character_limit_25")),
  ),
);
const { value: mailStopTL } = useField(
  "mailStopTL",
  computed(() => addressSchemas.createMaxLengthTypedSchema(mailStopFieldLimit, t("common.messages.character_limit_9"))),
);
const { value: taxExemptNoTL } = useField(
  "taxExemptNoTL",
  computed(() =>
    addressSchemas.createTrimmedMaxLengthTypedSchema(addressFieldLimit, t("common.messages.character_limit_35")),
  ),
);
const { value: countryCode } = useField("countryCode", addressSchemas.createCountryCodeTypedSchema());
const { value: countryName } = useField(
  "countryName",
  computed(() => addressSchemas.createMaxLengthTypedSchema(addressFieldLimit, t("common.messages.character_limit_35"))),
);
const { value: postalCode } = useField(
  "postalCode",
  computed(() =>
    addressSchemas.createPostalCodeTypedSchema({
      t,
      validatePostalCode,
      countryCode: countryCode.value ?? "",
      countryName: countryName.value ?? undefined,
    }),
  ),
);
const { value: countyTL } = useField(
  "countyTL",
  computed(() => addressSchemas.createMaxLengthTypedSchema(addressFieldLimit, t("common.messages.character_limit_35"))),
);
const { value: organization } = useField("organization", organizationRules);
const { value: districtTL } = useField(
  "districtTL",
  computed(() => addressSchemas.createMaxLengthTypedSchema(addressFieldLimit, t("common.messages.character_limit_35"))),
);
const { value: regionName } = useField(
  "regionName",
  computed(() => addressSchemas.createMaxLengthTypedSchema(addressFieldLimit, t("common.messages.character_limit_35"))),
);
const { value: regionId } = useField("regionId", regionRules);
const { value: line1 } = useField(
  "line1",
  computed(() => addressSchemas.createLine1TypedSchema({ t })),
);
const { value: isDefaultBillingAddressTL } = useField(
  "isDefaultBillingAddressTL",
  addressSchemas.createBooleanTypedSchema(),
);
const { value: isDefaultShippingAddressTL } = useField(
  "isDefaultShippingAddressTL",
  addressSchemas.createBooleanTypedSchema(),
);

onMounted(() => {
  isEditAddress.value = !!props.modelValue?.line1;

  if (!isCreateMode.value) {
    void validate();
  }

  if (values.line2 || values.departmentTL || values.mailStopTL || values.buildingTL) {
    addressLine2FormOpen.value = true;
  }

  if (values.taxExemptNoTL) {
    taxExemptNoFormOpen.value = true;
  }
});

function formatPostalCode(event: Event) {
  const input = event.target as HTMLInputElement;

  if (isJapan.value) {
    // Convert full-width numbers to half-width numbers
    let value = input.value.replace(/[０-９]/g, (char) => String.fromCharCode(char.charCodeAt(0) - 0xfee0));
    // Remove all non-numeric characters
    value = value.replace(/[^0-9]/g, "");
    if (value.length > 3) {
      value = value.slice(0, 3) + "-" + value.slice(3, 7);
    }

    input.value = value;
    postalCode.value = value;
    return;
  }

  if (isCanada.value) {
    // Canadian postal codes are formatted as: A1A 1A1
    // Keep only alphanumeric characters and insert a space after the 3rd character.
    let value = input.value.toUpperCase().replace(/[^0-9A-Z]/g, "");
    value = value.slice(0, 6);
    if (value.length > 3) {
      value = value.slice(0, 3) + " " + value.slice(3);
    }

    input.value = value;
    postalCode.value = value;
  }
}

watch(postalCode, async (newPostalCode) => {
  if (isEditAddress.value) {
    isEditAddress.value = false;
    return;
  }

  // Specific country codes should disable melissa autocomplete
  if (countryCode.value && countryCodesThatDisableMelissa.includes(countryCode.value)) {
    return;
  }

  if (newPostalCode && newPostalCode.length > 2) {
    city.value = "";
    regionId.value = "";
    melissaRegions.value = [];
    cities.value = [];

    try {
      const args: QueryGetMelissaAddressesArgs = {
        freeFormatInput: newPostalCode,
        countryCode: countryCode.value || undefined,
      };
      const result = await loadMelissaAddresses(args);

      if (result.length == 0) {
        return;
      }

      if (countryCode.value === "THA") {
        mapThailandFields(result);
      } else if (countryCode.value === "CHN") {
        mapChinaFields(result);
      } else if (countryCode.value === "TWN") {
        mapTaiwanFields(result);
      } else if (countryCode.value === "BRA") {
        mapBrazilFields(result);
      } else {
        mapFieldsBasedOnCountryConfig(result);
      }

      if (regions.value.length === 0) {
        melissaRegions.value.push({ id: result[0].administrativeArea!, name: result[0].administrativeArea! });
      }
    } catch (error) {
      Logger.error("Error fetching Melissa addresses:", error);
    }
  }
});

watch(countryCode, (newCountryCode) => {
  if (props.modelValue?.countryCode !== newCountryCode) {
    city.value = "";
    regionId.value = "";
    melissaRegions.value = [];
    postalCode.value = getPostalCodePrefix(newCountryCode ?? "");
    cities.value = [];
    districts.value = [];
    return;
  }

  // On the preserve case, we want to preserve the previouse values, except the case when some value are not required and displayed any more
  const config = countryConfigsMap[newCountryCode ?? ""] || countryConfigsMap["DEFAULT"];
  const cityRequiredForNewCountry = props.requiredCity && !!config?.cityLabel;
  if (!cityRequiredForNewCountry) {
    city.value = "";
  }
});

function mapFieldsBasedOnCountryConfig(melissaResult: MelissaResultType[]) {
  if (melissaResult.length == 1) {
    if (countryConfig.value?.cityLabel) {
      city.value = melissaResult[0].city || melissaResult[0].locality;
    }

    if (countryConfig.value?.stateLabel) {
      regionId.value = melissaResult[0].state || melissaResult[0].administrativeArea;
      regionName.value = melissaResult[0].state || melissaResult[0].administrativeArea;
    }

    if (countryConfig.value?.countyLabel) {
      countyTL.value = melissaResult[0].administrativeArea;
    }

    if (countryConfig.value?.districtLabel) {
      districtTL.value = melissaResult[0].locality;
    }
  } else {
    const uniqueCities = melissaResult
      .map((item, index) => ({
        id: index.toString(),
        name: item.city || item.locality,
      }))
      .filter((value, index, self) => value.name !== "" && self.findIndex((v) => v.name === value.name) === index);

    cities.value = uniqueCities as ItemSelectType[];
    if (countryConfig.value?.stateLabel) {
      regionId.value = melissaResult[0].state || melissaResult[0].administrativeArea;
      regionName.value = melissaResult[0].state || melissaResult[0].administrativeArea;
    }

    if (countryConfig.value?.countyLabel) {
      countyTL.value = melissaResult[0].administrativeArea;
    }
  }
}

function mapThailandFields(melissaResult: MelissaResultType[]) {
  if (melissaResult.length === 1) {
    districtTL.value = melissaResult[0].city || melissaResult[0].locality;
    regionId.value = melissaResult[0].administrativeArea;
    regionName.value = melissaResult[0].administrativeArea;
  } else {
    districts.value = melissaResult.map((item, index) => ({
      id: index.toString(),
      name: item.city || item.locality,
    })) as ItemSelectType[];
    regionId.value = melissaResult[0].administrativeArea;
    regionName.value = melissaResult[0].administrativeArea;
  }
}

function mapChinaFields(result: MelissaResultType[]) {
  if (result.length === 1) {
    city.value = result[0].locality;
    regionId.value = result[0].administrativeArea;
    regionName.value = result[0].administrativeArea;
    districtTL.value = result[0].dependentLocality;
  } else {
    cities.value = result.map((item, index) => ({
      id: index.toString(),
      name: item.city || item.locality,
    })) as ItemSelectType[];
    regionId.value = result[0].administrativeArea;
    regionName.value = result[0].administrativeArea;
    districtTL.value = result[0].dependentLocality;
  }
}

function mapTaiwanFields(result: MelissaResultType[]) {
  if (result.length === 1) {
    city.value = result[0].administrativeArea;
  } else {
    cities.value = result.map((item, index) => ({
      id: index.toString(),
      name: item.administrativeArea,
    })) as ItemSelectType[];
  }
}

function mapBrazilFields(melissaResult: MelissaResultType[]) {
  if (melissaResult.length === 1) {
    if (countryConfig.value?.cityLabel) {
      city.value = melissaResult[0].city || melissaResult[0].locality;
    }

    if (countryConfig.value?.stateLabel) {
      const rawRegion = melissaResult[0].state || melissaResult[0].administrativeArea;
      const capitalizedRegion = rawRegion ? rawRegion.toUpperCase() : "";
      regionId.value = capitalizedRegion;
      regionName.value = capitalizedRegion;
    }

    if (countryConfig.value?.countyLabel) {
      countyTL.value = melissaResult[0].administrativeArea;
    }
  }
}

const save = handleSubmit(
  (address) => {
    const newAddress: MemberAddressTypeExtension = { ...address, name: getAddressName(address) };

    // Ensure the country name is set if we have a country code
    // fix for Chrome when you pick an address from your google account and country name is empty
    if (newAddress.countryCode && (!newAddress.countryName || !newAddress.countryName.trim())) {
      const selectedCountry = props.countries.find((c) => c.id === newAddress.countryCode);
      if (selectedCountry) {
        newAddress.countryName = selectedCountry.name;
      }
    }

    emit("update:modelValue", newAddress);
    emit("save", newAddress);
  },
  async (context) => {
    Logger.debug(context);
    await scrollToFirstError();
  },
);

watch(
  () => props.modelValue,
  (modelValue) => {
    resetForm({ values: modelValue });
  },
  { deep: true, immediate: true },
);
</script>
